/* eslint-disable complexity */
import React, { useMemo, useCallback, useState } from 'react';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { TimeSeriesTrendsRequest } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsTypes';
import { HorizontalFilterBar } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { HorizontalFilterBarItemProps } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { HorizontalFilterBarItem } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useFilters } from 'apps/sigma2-ui/src/filters-v2/lib/useFilters';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { getPrimaryFilterFullKey } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getDateRangeFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSecondaryFilterSelectionsFromCustomVizFormat } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SecondaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { GetCustomVizRequestArgs } from 'apps/sigma2-ui/src/filters-v2/components/SaveAnalysisButton';
import { useShareLinkRequest } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { ProgressBarChartWithDatesCard } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressBarChartCard';
import { getTimeSeriesTrendsPrimaryFilterSelections } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressBarChartCard';
import { EventStudyButton } from 'apps/sigma2-ui/src/filters-v2/components/EventStudyButton';
import { EventStudyMenu } from 'apps/sigma2-ui/src/filters-v2/components/EventStudyMenu';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { isEventStudyEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { DataRefreshIndicator } from 'apps/sigma2-ui/src/components/custom-viz/DataRefreshIndicator';
import { buildTimeSeriesTrendsViz } from 'apps/sigma2-ui/src/filters-v2/components/getCustomVizFromFilters';
import { ViewConfigurationSchema } from 'libs/end-to-end/src/view-configurations';

//eslint-disable-next-line complexity
export const ProgressBarChartWithDates = () => {
  const { myObservation, selectedOrganizationId } = useObservation();

  if (!myObservation || !selectedOrganizationId) {
    throw new Error(
      'Observation or organization ID is not available. Please ensure you have selected an observation and organization.',
    );
  }

  const context = useCustomViz();

  const { doViz } = context;

  const [viewConfiguration, setViewConfiguration] =
    useState<ViewConfigurationSchema>({});

  const isEventStudyEnabled = useMemo(
    () => isEventStudyEnabledForRevision({ revisionFlags: myObservation }),
    [myObservation],
  );

  const getCustomVizRequestWithEverything = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: {
      primaryFilterSelections: PrimaryFilterSelection[];
      secondaryFilterSelections: SecondaryFilterSelection[];
    }): TimeSeriesTrendsRequest | null => {
      return buildTimeSeriesTrendsViz({
        feature: 'timeSeriesTrendsPrimary',
        primaryFilterSelections,
        secondaryFilterSelections,
        myObservation,
        organizationId: selectedOrganizationId,
      });
    },
    [myObservation, selectedOrganizationId],
  );

  const getCustomVizRequestForFilters = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: Pick<
      GetCustomVizRequestArgs,
      'primaryFilterSelections' | 'secondaryFilterSelections'
    >): {
      customVizRequest: TimeSeriesTrendsRequest | undefined;
      viewConfiguration?: ViewConfigurationSchema;
    } => {
      const customVizRequest: TimeSeriesTrendsRequest | null =
        getCustomVizRequestWithEverything({
          primaryFilterSelections,
          secondaryFilterSelections,
        });

      return {
        customVizRequest: customVizRequest ?? undefined,
        viewConfiguration,
      };
    },
    [getCustomVizRequestWithEverything, viewConfiguration],
  );

  const onAnalyzeForFilters = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: {
      primaryFilterSelections: PrimaryFilterSelection[];
      secondaryFilterSelections: SecondaryFilterSelection[];
    }) => {
      const { customVizRequest } = getCustomVizRequestForFilters({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      if (!customVizRequest) {
        return;
      }

      doViz(customVizRequest);

      setViewConfiguration((oldConfig) => ({
        ...oldConfig,
        overallDisplayMode: undefined,
      }));
    },
    [doViz, getCustomVizRequestForFilters],
  );

  const filterPayload = useFilters({
    feature: 'timeSeriesTrendsPrimary',
    focusArea: null,
    disabled: false,
    metricDefinitionWhiteLabeling: null,
    getCustomVizRequest: getCustomVizRequestForFilters,
    analysisName: undefined,
    onApply: ({ primaryFilterSelections, secondaryFilterSelections }) => {
      onAnalyzeForFilters({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
  });

  const latestRequest = useMemo(
    () =>
      getCustomVizRequestWithEverything({
        primaryFilterSelections: filterPayload.primaryFilterSelections,
        secondaryFilterSelections: filterPayload.secondaryFilterSelections,
      }),
    [
      filterPayload.primaryFilterSelections,
      filterPayload.secondaryFilterSelections,
      getCustomVizRequestWithEverything,
    ],
  );

  const { userAuthenticatedApiCall, myUser } = useUser();

  const onShareLinkLoaded = useCallback(
    (request: CustomVizRequest | null) => {
      if (
        !request ||
        !myObservation ||
        request.customVizId !== 'timeSeriesTrends'
      ) {
        return;
      }

      const extras = request.inputs.extras;
      const filters = extras.filters;
      const metric = extras.metric;
      const breakdownOption = extras.breakdownOption;

      const focusArea = myObservation.focusAreas.find((fa) =>
        fa.metrics.some((m) => m.columnHeaderValue === metric),
      )?.focusArea;

      if (!focusArea || !breakdownOption) {
        return;
      }

      const secondaryFilterSelections =
        getSecondaryFilterSelectionsFromCustomVizFormat({
          feature: 'timeSeriesTrendsPrimary',
          focusArea: focusArea,
          customVizFilters: filters,
          focusAreas: myObservation.focusAreas,
          filterSetDefinitionsWithValues:
            myObservation.filterSetDefinitionsWithValues,
          dimensionDefinitions: myObservation.dimensions,
          analysisName: undefined,
          pipelineAnalysisConfiguration:
            myObservation.pipelineAnalysisConfiguration,
        });

      const primaryFilterSelections =
        getTimeSeriesTrendsPrimaryFilterSelections({
          customVizRequest: request,
          myObservation,
        });

      if (!primaryFilterSelections) {
        return;
      }

      filterPayload.setFilters({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
    [filterPayload, myObservation],
  );

  useShareLinkRequest({
    onLoaded: onShareLinkLoaded,
    myUser,
    canLoad: filterPayload.isInitialized,
    userAuthenticatedApiCall,
    type: TimeSeriesTrendsRequest,
  });

  const isDoingEventStudy = useMemo(
    () => isEventStudyEnabled && latestRequest?.inputs.extras.treatmentDate,
    [isEventStudyEnabled, latestRequest?.inputs.extras.treatmentDate],
  );

  const topFilterBarItems = useMemo(() => {
    return filterPayload.items.filter((item) => {
      if (
        isDoingEventStudy &&
        item.key ===
          getPrimaryFilterFullKey({
            primaryFilterKey: 'bigTimePeriodComparison',
          })
      ) {
        return false;
      }

      return (
        item.key !==
          getPrimaryFilterFullKey({ primaryFilterKey: 'dateRange' }) &&
        item.key !==
          getPrimaryFilterFullKey({ primaryFilterKey: 'chartType' }) &&
        item.key !==
          getPrimaryFilterFullKey({ primaryFilterKey: 'singleDate' }) &&
        item.key !==
          getPrimaryFilterFullKey({ primaryFilterKey: 'eventStudyGroup' })
      );
    });
  }, [filterPayload.items, isDoingEventStudy]);

  const bottomFilterBarItems = useMemo(() => {
    const selection = getDateRangeFromPrimaryFilterValues({
      primaryFilterValues: filterPayload.primaryFilterSelections,
    });

    const from = selection?.startDate;
    const to = selection?.endDate;
    const selectedPeriod = selection?.dateRangeType;

    return [
      ...filterPayload.items.filter(
        (item) =>
          item.key ===
          getPrimaryFilterFullKey({ primaryFilterKey: 'dateRange' }),
      ),
      {
        key: 'dateRangeDisplay',
        content: () => {
          if (selection?.dateRangeType === 'custom') {
            return null;
          }

          const formattedRange =
            selectedPeriod === 'all'
              ? 'All'
              : from && to
                ? `${from.toLocaleDateString()} - ${to.toLocaleDateString()}`
                : 'No date range selected';

          return (
            <div
              className="flex items-center justify-center h-full"
              style={{
                minHeight: '40px',
                padding: '0 8px',
              }}
            >
              <S2Typography
                variant="body-6"
                translatedContent={formattedRange}
                textColorVariant="gray-600"
              />
            </div>
          );
        },
        menu: () => null,
      },
    ];
  }, [filterPayload.items, filterPayload.primaryFilterSelections]);

  const singleDateItem = useMemo(() => {
    return filterPayload.items.find(
      (item) =>
        item.key ===
        getPrimaryFilterFullKey({ primaryFilterKey: 'singleDate' }),
    );
  }, [filterPayload.items]);

  const eventStudyGroupItem = useMemo(() => {
    return filterPayload.items.find(
      (item) =>
        item.key ===
        getPrimaryFilterFullKey({ primaryFilterKey: 'eventStudyGroup' }),
    );
  }, [filterPayload.items]);

  const eventStudyItem: HorizontalFilterBarItemProps | undefined =
    useMemo(() => {
      if (!singleDateItem || !eventStudyGroupItem) {
        return;
      }

      return {
        content: ({ openMenu }) => (
          <EventStudyButton disabled={false} onClick={openMenu} />
        ),
        menu: ({ closeMenu }) => (
          <EventStudyMenu
            filterPayload={filterPayload}
            singleDateItem={singleDateItem}
            eventStudyGroupItem={eventStudyGroupItem}
            closeMenu={closeMenu}
          />
        ),
        key: 'eventStudy',
      };
    }, [eventStudyGroupItem, filterPayload, singleDateItem]);

  const chartTypeItem = useMemo(() => {
    if (isDoingEventStudy) {
      return;
    }

    const item = filterPayload.items.find(
      (item) => item.key === 'primary:chartType',
    );
    return item?.content({
      openMenu: () => {},
      menuOpened: false,
    });
  }, [filterPayload.items, isDoingEventStudy]);

  const [eventStudyMenuOpen, setEventStudyMenuOpen] = useState(false);

  return (
    <div className="w-full h-full min-h-96">
      <UserAnalysesOverlay onUserAnalysisCancel={() => {}} />
      <Spacing size={'md'} direction={'vertical'}>
        <HorizontalFilterBar
          items={topFilterBarItems}
          slotRight={
            <DataRefreshIndicator
              status={context.status}
              resultCreatedAt={context.resultCreatedAt}
            />
          }
        />

        <Card>
          <Spacing direction="vertical" size="md">
            <Spacing
              size={'md'}
              direction={'horizontal'}
              className="items-center justify-between flex-wrap"
            >
              <Spacing
                direction="horizontal"
                size="sm"
                className="items-center"
              >
                {bottomFilterBarItems.map((bottomFilterBarItem, i) => (
                  <HorizontalFilterBarItem
                    {...bottomFilterBarItem}
                    key={`bottom-filter-bar-item-${i}`}
                    menuOpened={false}
                    openMenu={() => {}}
                    closeMenu={() => {}}
                  />
                ))}
              </Spacing>
              {isEventStudyEnabled && eventStudyItem && (
                <HorizontalFilterBarItem
                  {...eventStudyItem}
                  key={`event-study-filter-bar-item`}
                  menuOpened={eventStudyMenuOpen}
                  openMenu={() => setEventStudyMenuOpen(true)}
                  closeMenu={() => setEventStudyMenuOpen(false)}
                />
              )}
            </Spacing>

            {chartTypeItem}

            <ProgressBarChartWithDatesCard
              customVizRequest={
                (context.lastSuccessfulCustomVizRequest as TimeSeriesTrendsRequest | null) ??
                undefined
              }
              result={context.result}
              status={context.status}
              viewConfiguration={viewConfiguration}
              setViewConfiguration={setViewConfiguration}
              noPadding={true}
            />
          </Spacing>
        </Card>
      </Spacing>
    </div>
  );
};
